import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { Browser, type OpenOptions } from '@capacitor/browser';
import { App, type URLOpenListenerEvent } from '@capacitor/app';
import { Preferences, type SetOptions } from '@capacitor/preferences';
import type { App as VueApp, ObjectPlugin } from 'vue';

// Get the name of the Platform the app is currently running on.
// Return a value of "web", "ios", or "android".
const getPlatform = () => Capacitor.getPlatform();

// Check whether the currently running platform is native.
// This function returns a value of "true" if the app is running as a native, installed Capacitor app,
// or false if it is served via a browser or installed as a PWA.
const isNativePlatform = () => Capacitor.isNativePlatform();

// Return whether app is running on iOS
const isIos = () => Capacitor.getPlatform() === 'ios';

// Return whether app is running on Android
const isAndroid = () => Capacitor.getPlatform() === 'android';

// Return whether app is running on Web
const isWeb = () => Capacitor.getPlatform() === 'web';

// see https://capacitorjs.com/docs/apis/browser#openoptions
const openBrowser = async (options: OpenOptions) => await Browser.open(options);

// Return information about the app. see https://capacitorjs.com/docs/apis/app
const getInfo = async () => await App.getInfo();

// Get device information. see https://capacitorjs.com/docs/apis/device
const getDeviceInfo = async () => await Device.getInfo();

// Get device ID. see https://capacitorjs.com/docs/apis/device
const getDeviceId = async () => await Device.getId();

// Exit App
const exitOutOfApp = async () => await App.exitApp();

// Return app version. On iOS:CFBundleShortVersionString. On Android: versionName.
const getAppVersion = async () => {
  const info = await App.getInfo();
  return info.version;
};

// see https://capacitorjs.com/docs/apis/preferences
const getPreference = async (key: string) => {
  const { value } = await Preferences.get({ key });
  return value; // ? If a value was not previously set or was removed, value will be null.
};

// see https://capacitorjs.com/docs/apis/preferences
const setPreference = async ({ key, value }: SetOptions) => await Preferences.set({ key, value });

// see https://capacitorjs.com/docs/apis/preferences
const removePreference = async (key: string) => await Preferences.remove({ key });

// see https://capacitorjs.com/docs/apis/app#pause
const addListenerOnAppPause = async (cb: () => void) =>
  await App.addListener('pause', () => {
    cb();
  });

// see https://capacitorjs.com/docs/apis/app#resume
const addListenerOnAppResume = async (cb: () => void) =>
  await App.addListener('resume', () => {
    cb();
  });

// see https://capacitorjs.com/docs/apis/app#urlopen
const addListenerOnAppUrlOpen = (cb: (event: URLOpenListenerEvent) => void) => {
  // * Listen for url open events for the app and handle deep link
  return App.addListener('appUrlOpen', cb); // capacitor v6 and below
};
// see https://capacitorjs.com/docs/apis/browser#open
const _wrapper = {
  Capacitor,
  getPlatform,
  isNativePlatform,
  isIos,
  isAndroid,
  isWeb,
  openBrowser,
  getDeviceId,
  getInfo,
  getDeviceInfo,
  exitOutOfApp,
  getAppVersion,
  getPreference,
  setPreference,
  removePreference,
  addListenerOnAppPause,
  addListenerOnAppResume,
  addListenerOnAppUrlOpen,
};

export const CapacitorFacade = _wrapper;

export const capacitorPlugin: ObjectPlugin = {
  install(vueApp: VueApp) {
    if (vueApp.config.globalProperties.$pc2capacitor !== _wrapper) {
      vueApp.config.globalProperties.$pc2capacitor = _wrapper;
    }
  },
};
